import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchLegalDocument } from "../../services/legal.services";
import LegalFooter from "../../Components/Auth/Legal/LegalFooter";
import {
  DOCUMENT_NAMES,
  LEGAL_PATHS,
  formatEffectiveDate,
  versionPath,
} from "./legalPaths";
import "./LegalPage.css";

const TEXT = {
  fr: {
    version: "Version",
    effective: "En vigueur le",
    previous: "Versions précédentes",
    archived: "Version archivée",
    archivedNote:
      "Texte conservé tel que publié sur hajexbolt.com; en anglais seulement.",
    current: "Version en vigueur",
    loading: "Chargement…",
    missing: "Ce document n'est pas disponible pour le moment.",
  },
  en: {
    version: "Version",
    effective: "Effective",
    previous: "Previous versions",
    archived: "Archived version",
    archivedNote: "Text kept as published on hajexbolt.com; English only.",
    current: "Current version",
    loading: "Loading…",
    missing: "This document isn't available right now.",
  },
};

// Archived pages stay reachable but out of search engines.
const useNoIndex = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, [active]);
};

/**
 * Public page for the Terms of Service or the Privacy Policy, in French or English,
 * current or a given version (hand-off 4.1). Works logged in or out.
 */
export const LegalPage = ({ type, language, version }) => {
  const [state, setState] = useState({ loading: true, doc: null });
  const t = TEXT[language] ?? TEXT.en;
  const otherLanguage = language === "fr" ? "en" : "fr";

  useEffect(() => {
    let alive = true;
    setState({ loading: true, doc: null });
    fetchLegalDocument(type, language, version).then((res) => {
      if (alive) {
        setState({
          loading: false,
          doc: res.error ? null : (res.data?.result ?? null),
        });
      }
    });
    return () => {
      alive = false;
    };
  }, [type, language, version]);

  const doc = state.doc;
  useNoIndex(Boolean(doc?.is_archive));
  useEffect(() => {
    if (doc?.title) document.title = `${doc.title} | HAJEX Bolt`;
  }, [doc]);

  const others = (doc?.versions ?? []).filter(
    (v) => !(v.version === doc.version && v.language === doc.language),
  );
  // On a current page: the archives and older versions in its language. On an
  // archive: the other archives (the banner links to the current version).
  const previous = others.filter((v) =>
    doc?.is_archive ? v.is_archive : v.is_archive || v.language === language,
  );

  return (
    <div className="legal-page min-h-screen bg-ui-surface-sunken">
      <header className="bg-ui-surface border-b border-ui-border">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="font-bold text-brand text-lg no-underline">
            HAJEX Bolt
          </Link>
          <nav className="text-sm" aria-label="Langue / Language">
            {["fr", "en"].map((code, i) => (
              <React.Fragment key={code}>
                {i > 0 && <span className="text-ui-text-subtle"> | </span>}
                {code === language && !doc?.is_archive ? (
                  <span className="font-semibold text-ui-text">
                    {code === "fr" ? "Français" : "English"}
                  </span>
                ) : (
                  <Link
                    to={LEGAL_PATHS[type][code]}
                    className="text-brand hover:underline"
                  >
                    {code === "fr" ? "Français" : "English"}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {state.loading && <p className="text-ui-text-muted">{t.loading}</p>}
        {!state.loading && !doc && <p className="text-ui-text">{t.missing}</p>}
        {doc && (
          <article className="bg-ui-surface rounded-xl border border-ui-border p-5 md:p-8">
            {doc.is_archive && (
              <div className="legal-archive-banner mb-4" role="note">
                <strong>
                  {TEXT.fr.archived} / {TEXT.en.archived}
                </strong>
                {" — "}
                {doc.title}. {TEXT.fr.archivedNote} / {TEXT.en.archivedNote}{" "}
                <Link to={LEGAL_PATHS[type].fr}>{TEXT.fr.current}</Link>
                {" / "}
                <Link to={LEGAL_PATHS[type].en}>{TEXT.en.current}</Link>
              </div>
            )}
            <dl className="legal-meta">
              <div>
                <dt>{t.version}</dt>
                <dd>{doc.version}</dd>
              </div>
              {!doc.is_archive && (
                <div>
                  <dt>{t.effective}</dt>
                  <dd>{formatEffectiveDate(doc.effective_date, language)}</dd>
                </div>
              )}
              {!doc.is_archive && (
                <div>
                  <dt>
                    {otherLanguage === "fr"
                      ? "Version française"
                      : "English version"}
                  </dt>
                  <dd>
                    <Link to={LEGAL_PATHS[type][otherLanguage]}>
                      {DOCUMENT_NAMES[type][otherLanguage]}
                    </Link>
                  </dd>
                </div>
              )}
            </dl>
            <div
              className="legal-body"
              // The text comes from HAJEX Bolt's own legal_documents table.
              dangerouslySetInnerHTML={{ __html: doc.body_html }}
            />
            {previous.length > 0 && (
              <section className="legal-previous">
                <h2>{t.previous}</h2>
                <ul>
                  {previous.map((v) => (
                    <li key={`${v.version}-${v.language}`}>
                      <Link to={versionPath(type, v.language, v.version)}>
                        {v.title} ({v.version}
                        {v.is_archive ? "" : `, ${v.language.toUpperCase()}`})
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </article>
        )}
        <LegalFooter className="mt-6 text-center" />
      </main>
    </div>
  );
};

/** /legal/:type/:language/:version */
export const LegalVersionPage = () => {
  const { type, language, version } = useParams();
  return <LegalPage type={type} language={language} version={version} />;
};

export default LegalPage;
